import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { UserService } from '@duck/api-definitions'
import { Breadcrumbs, Tabs, TabsList, TabsTrigger, TabsContent } from '@duck/ui'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'
import { useUserDetails } from '@/hooks/useUserDetails'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useAdminUsers } from '@/hooks/useAdminUsers'
import { deriveAccessGroups } from '../userAdmin.utils'
import { OverviewSection } from '../UserDetailSections/OverviewSection'
import { AssignmentsSection } from '../UserDetailSections/AssignmentsSection'
import { ChallengesSection } from '../UserDetailSections/ChallengesSection'
import { SettingsSection } from '../UserDetailSections/SettingsSection'
import { ActionsSection } from '../UserDetailSections/ActionsSection'
import { UserDetailSkeleton } from '../UserDetailSections/UserDetailSkeleton'

interface UserDetailPageProps {
  id: string
}

export const UserDetailPage: React.FC<UserDetailPageProps> = ({ id }) => {
  const { t } = useTar()
  const navigate = useNavigate()

  const { details, isLoading, isError, refetch } = useUserDetails(id)

  const userName = details?.user
    ? `${details.user.firstName} ${details.user.lastName}`.trim()
    : ''
  useDocumentTitle(userName || id, 'admin')

  const { users } = useAdminUsers({ queryFilters: {} })
  const allAccessGroups = useMemo(() => deriveAccessGroups(users), [users])

  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [overviewData, setOverviewData] = useState<Partial<UserService.User>>({})
  const [settingsData, setSettingsData] = useState<Partial<UserService.UserSettings>>({})

  useEffect(() => {
    if (details?.user) {
      setOverviewData({ ...details.user })
      setSettingsData({ ...details.user.settings })
    }
    setSaveError(null)
    setIsSaving(false)
  }, [details])

  const handleOverviewChange = useCallback(
    (field: keyof UserService.User, value: unknown) => {
      setOverviewData((prev) => ({ ...prev, [field]: value }))
    },
    []
  )

  const handlePersonalInfoChange = useCallback(
    (field: string, value: unknown) => {
      setOverviewData((prev) => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, [field]: value },
      }))
    },
    []
  )

  const handleSettingsChange = useCallback(
    (field: keyof UserService.UserSettings, value: unknown) => {
      setSettingsData((prev) => ({ ...prev, [field]: value }))
    },
    []
  )

  const handleSave = async () => {
    if (!id || !details?.user) return
    setSaveError(null)

    if (!overviewData.email || !overviewData.firstName || !overviewData.lastName) {
      setSaveError(t('users.validationNameEmailRequired'))
      return
    }
    if (!overviewData.accessGroups || overviewData.accessGroups.length === 0) {
      setSaveError(t('users.validationAccessGroupRequired'))
      return
    }

    try {
      setIsSaving(true)
      const updateData: UserService.UpdateUserRequest = {
        email: overviewData.email,
        firstName: overviewData.firstName,
        lastName: overviewData.lastName,
        role: overviewData.role as UserService.UserRole,
        organizationId: overviewData.organizationId,
        isActive: overviewData.isActive,
        isVerified: overviewData.isVerified,
        accessGroups: overviewData.accessGroups as string[],
        notes: overviewData.notes,
        badges: overviewData.badges,
        source: overviewData.source,
        personalInfo: overviewData.personalInfo,
        settings: settingsData,
      }
      await apis.userService.idPut(id, updateData)
      void refetch()
      toast.success(t('users.userUpdated'))
    } catch (error: unknown) {
      console.error('Failed to save user:', error)
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('users.saveFailed')
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('challenges.breadcrumbAdmin'), href: '/admin' },
          { label: t('users.userManagement'), href: '/admin/users' },
          {
            label:
              details?.user
                ? `${details.user.firstName} ${details.user.lastName}`
                : id,
          },
        ]}
        renderLink={(item, children) => <Link to={item.href}>{children}</Link>}
      />

      {isLoading && <UserDetailSkeleton />}

      {isError && (
        <div className="rounded-md bg-error/10 border border-error/20 p-4">
          <div className="text-error text-sm">{t('common.error')}</div>
        </div>
      )}

      {details && !isLoading && (
        <>
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-foreground">
              {`${details.user.firstName} ${details.user.lastName}`}
            </h2>
            <button
              onClick={() => { void handleSave() }}
              disabled={isSaving}
              className="px-4 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isSaving ? t('common.saving') : t('common.save')}
            </button>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">{t('common.overview')}</TabsTrigger>
              <TabsTrigger value="assignments">{t('partner.assignments')}</TabsTrigger>
              <TabsTrigger value="challenges">{t('navigation.challenges')}</TabsTrigger>
              <TabsTrigger value="settings">{t('common.settings')}</TabsTrigger>
              <TabsTrigger value="actions">{t('common.actions')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4">
              <OverviewSection
                formData={overviewData}
                onChange={handleOverviewChange}
                onPersonalInfoChange={handlePersonalInfoChange}
                allAccessGroups={allAccessGroups}
                user={details.user}
                challenges={details.challenges}
              />
            </TabsContent>

            <TabsContent value="assignments" className="mt-4">
              <AssignmentsSection
                assignments={details.assignments}
                onAssignmentChange={() => { void refetch() }}
              />
            </TabsContent>

            <TabsContent value="challenges" className="mt-4">
              <ChallengesSection challenges={details.challenges} />
            </TabsContent>

            <TabsContent value="settings" className="mt-4">
              <SettingsSection
                settingsData={settingsData}
                onChange={handleSettingsChange}
              />
            </TabsContent>

            <TabsContent value="actions" className="mt-4">
              <ActionsSection
                userId={id}
                userName={`${details.user.firstName} ${details.user.lastName}`}
                userRole={details.user.role}
                onRoleChanged={() => { void refetch() }}
                onUserDeleted={() => { void navigate({ to: '/admin/users' }) }}
              />
            </TabsContent>
          </Tabs>

          {saveError && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md">
              <p className="text-sm text-error">{saveError}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
